import { apiClient } from "../../../services/axiosInstance";
import { ServerListResponse, ServerSingleResponse } from "../../../types";
import {
  OwnerPaymentServiceTokenTypes,
  SubscriptionOffersTypes,
  SubscriptionOffersV2Type,
} from "../types";

export const GET_SUBSCRIPTION_PRICING_QUERY_KEY = ["get-subscription-pricing"];
export function getSubscriptionPricing(promoCode?: string) {
  return apiClient.get<ServerSingleResponse<SubscriptionOffersTypes>>(
    `/v1/account/webnext-subscription-pricing?code=${promoCode ?? ""}`,
  );
}

export const GET_SUBSCRIPTION_PRICING_V2_QUERY_KEY = [
  "get-subscription-pricing-v2",
];
export function getSubscriptionPricingV2(
  promoCode?: string,
  userOfferUuid?: string,
) {
  return apiClient.post<ServerSingleResponse<SubscriptionOffersV2Type>>(
    `/v1/account/subscription-pricing-v2`,
    {
      meta: {},
      payload: {
        promo_code: promoCode || undefined,
        user_offer_uuid: userOfferUuid || undefined,
      },
    },
  );
}

export const GET_GIFTCARD_PRICING_QUERY_KEY = ["get-giftcard-pricing"];
export function getGiftCardPricing(promoCode?: string) {
  return apiClient.get<ServerSingleResponse<SubscriptionOffersTypes>>(
    `/v1/account/webnext-giftcard-pricing?code=${promoCode ?? ""}`,
  );
}

export type WebPurchasePayloadTypes = {
  accessTokenTypeId: number;
  totalMinorUnits: number;
  stripeToken?: string;
  ownerPaymentServiceTokenId?: string;
  incentiveCode?: string | null;
};
export function webPurchase(payload: WebPurchasePayloadTypes) {
  return apiClient.post<ServerSingleResponse<any>>(`/v1/account/web-purchase`, {
    meta: {},
    payload,
  });
}

export const GET_SAVED_PAYMENT_METHODS_QUERY_KEY = [
  "get-saved-payment-methods",
];
export function getSavedPaymentMethods() {
  return apiClient.get<ServerListResponse<OwnerPaymentServiceTokenTypes>>(
    `/v1/account/payment-methods`,
  );
}

export type ReleasePurchasePayload = {
  price_point_id: number;
  raw_price: number;
  is_gold: boolean;
  stripeToken?: string;
  ownerPaymentServiceTokenId?: string;
  full_price?: number;
  is_sale?: boolean;
};
export function unlockRelease(
  releaseId: number,
  payload: ReleasePurchasePayload,
) {
  return apiClient.post<ServerSingleResponse<any>>(
    `/v1/releases/${releaseId}/unlock`,
    {
      meta: {},
      payload: payload,
    },
  );
}

// POD (Print-on-Demand) purchase types
export interface ShippingAddress {
  first_name: string;
  last_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
  phone_number?: string;
}

export interface PodPurchasePayload extends ShippingAddress {
  product_id: number;
  price: number;
  quantity?: number;
  stripe_token?: string;
  owner_payment_service_token_id?: number;
  save_address?: boolean;
  taxes_shipping: string; // JWT proof token from tax-shipping endpoint
}

// Tax-shipping request/response types
export interface TaxShippingRequest {
  product_id: number;
  price: number;
  quantity?: number;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
  currency_code?: string;
}

export interface TaxShippingAddress {
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  zip: string;
  country_id: string;
}

export interface TaxShippingResponse {
  product_id: number;
  address: TaxShippingAddress;
  price: number;
  quantity: number;
  currency: string;
  shipping: number;
  taxes: number;
  proof: string; // JWT token
}

export function getTaxAndShipping(payload: TaxShippingRequest) {
  return apiClient.post<ServerSingleResponse<TaxShippingResponse>>(
    `/v1/pod/tax-shipping`,
    { meta: {}, payload },
  );
}

export interface PodPurchaseResponse {
  status: string;
  shipping_address: ShippingAddress;
  estimated_delivery: string;
}

export function purchasePod(payload: PodPurchasePayload) {
  return apiClient.post<ServerSingleResponse<PodPurchaseResponse>>(
    `/v1/pod/purchase`,
    {
      meta: {},
      payload,
    },
  );
}

// Saved address types
export interface SavedAddress {
  id: number;
  address_type_id: number;
  display_name: string;
  firstname: string;
  lastname: string;
  phone_number: string;
  address_line1: string;
  address_line2: string;
  address_line3: string;
  city: string;
  state: string;
  zip: string;
  country_id: string;
}

export const GET_SAVED_ADDRESSES_QUERY_KEY = ["get-saved-addresses"];
export function getSavedAddresses() {
  return apiClient.get<ServerSingleResponse<SavedAddress>>(
    `/v1/account/address`,
  );
}
